/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BEST SELLERS PAGE - VERSION 5.2 DYNAMIC
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import Layout from '../Layout/Layout';
import LoadingSwiper from '../slider/loadindSwiper';
import useDataLoader from '../pageInside/exeemple_page/DataLoader';
import DynamicPageRenderer from '../pageInside/exeemple_page/DynamicPageRenderer';
import { getInfoSourceUpdates } from '../pageInside/exeemple_page/allFacets';
import {
    PAGES_CONFIG_,
    SECTIONS_LIBRARY,
} from '../pageInside/exeemple_page/pagesConfig';
import { prices } from '../pageInside/fixedPrices';
import { getProductGridLimit } from '../../utils/paginationSettings';


// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES MODULE — stables, utilisables dans useCallback sans deps
// ═══════════════════════════════════════════════════════════════════════════

const PAGE_ID = 'bestSellers';
const DEFAULT_LIMIT = 12; // ← ancien fallback fixe, conservé comme valeur de repli

// ═══════════════════════════════════════════════════════════════════════════
// NIVEAU 3 — defaults depuis PAGES_CONFIG_.bestSellers
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_FACETS = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];
    if (!config?.sections?.length) {
        return ['bestSellersGrid', 'followMe'];
    }
    return config.sections
        .filter(s => s.enabled !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(s => s.sectionId);
})();

const DEFAULT_CONFIG_OVERRIDES = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];
    if (!config?.sections?.length) return {};

    const result = {};
    config.sections.forEach(s => {
        if (!s.sectionId) return;
        const entry = {};
        const lib = SECTIONS_LIBRARY[s.sectionId];

        if (lib?.defaultProps || s.props) {
            entry.props = { ...(lib?.defaultProps || {}), ...(s.props || {}) };
        }
        if (s.overrides && Object.keys(s.overrides).length > 0) {
            entry.overrides = { ...s.overrides };
        }
        if (Object.keys(entry).length > 0) {
            result[s.sectionId] = entry;
        }
    });
    return result;
})();

// ═══════════════════════════════════════════════════════════════════════════
// _extractFromSettings — NIVEAUX 1 & 2
// ═══════════════════════════════════════════════════════════════════════════

const _extractFromSettings = (settings) => {
    if (!settings || typeof settings !== 'object') return null;

    const { sections, facets } = settings;

    if (Array.isArray(sections) && sections.length > 0) {
        const sorted = [...sections]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        const resolvedFacets = sorted
            .filter(s => s.sectionId && s.enabled !== false)
            .map(s => s.sectionId);

        const configOverrides = {};
        sorted.forEach(s => {
            if (!s.sectionId) return;
            const entry = {};
            if (s.props     && Object.keys(s.props).length > 0)     entry.props     = s.props;
            if (s.overrides && Object.keys(s.overrides).length > 0) entry.overrides = s.overrides;
            if (Object.keys(entry).length > 0) configOverrides[s.sectionId] = entry;
        });

        if (resolvedFacets.length > 0) {
            return { facets: resolvedFacets, configOverrides };
        }
    }

    if (Array.isArray(facets) && facets.length > 0) {
        return { facets, configOverrides: {} };
    }

    return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// resolvePageConfig
// ═══════════════════════════════════════════════════════════════════════════

const resolvePageConfig = (pageSettings) => {
    const result = _extractFromSettings(pageSettings);
    if (result) return result;

    return {
        facets:          DEFAULT_FACETS,
        configOverrides: DEFAULT_CONFIG_OVERRIDES,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT
// ═══════════════════════════════════════════════════════════════════════════

const BestSellers = (props) => {
    const {
        infoSource,
        laguangeElakay,
        homeStyles,
    } = props;

    const token = infoSource?.token || null;

    // ─── Filtres ──────────────────────────────────────────────────────────────
    const [myFilters, setMyFilters] = useState({ filters: {} });

    const handlePrice = useCallback((value) => {
        let array = [];
        for (let key in prices) {
            if (prices[key]._id === parseInt(value)) {
                array = prices[key].array;
            }
        }
        return array;
    }, []);

    const handleFilters = useCallback((filters, filterBy) => {
        setMyFilters(prev => {
            const newFilters   = { ...prev };
            newFilters.filters = { ...prev.filters };

            if (filterBy === 'price') {
                newFilters.filters[filterBy] = handlePrice(filters);
            } else {
                newFilters.filters[filterBy] = filters;
            }

            return newFilters;
        });
        setSkip(0);
    }, [handlePrice]);

    // ─── Pagination ───────────────────────────────────────────────────────────
    const [skip, setSkip] = useState(0);
    // Admin-configurable via AnimationSettings → General → "Pagination &
    // Limites" (productGrids.productGridPage.limit) — same shared bucket as
    // New Products / Product Catalog's grid, so Main Pages and Category
    // Pages stay coordinated instead of Best Sellers having its own
    // disconnected hardcoded value. Falls back to 12 (the previous
    // hardcoded value) if nothing is configured.
    const limit = getProductGridLimit(infoSource?.allSettings, 'productGridPage', DEFAULT_LIMIT);

    // ─── CASCADE ──────────────────────────────────────────────────────────────
    const pageSettings = infoSource?.allSettings?.pages?.[PAGE_ID];

    const { facets, configOverrides } = useMemo(
        () => resolvePageConfig(pageSettings),
        [pageSettings]
    );

    // ─── DataLoader ───────────────────────────────────────────────────────────
    const {
        pageData,
        loading,
        mergePageData,
        loadPageData,
        reloadFacets,
    } = useDataLoader({
        facets,
        infoSource,
        token,
        skip,
        limit,
        filters: myFilters.filters,
    });

    // ─── Sync infoSource → pageData ───────────────────────────────────────────
    useEffect(() => {
        const updates = getInfoSourceUpdates(facets, infoSource);
        if (Object.keys(updates).length > 0) {
            mergePageData(updates);
        }
    }, [infoSource, facets]);

    // Si l'admin change la limite Pagination & Limites pendant que la page
    // est déjà ouverte, repartir de la page 1 plutôt que de garder un skip
    // qui ne correspond plus à la nouvelle taille de page.
    useEffect(() => {
        setSkip(0);
    }, [limit]);

    // ─── Recharger quand facets / skip / filtres changent ────────────────────
    useEffect(() => {
        loadPageData();
    }, [JSON.stringify(facets), JSON.stringify(myFilters.filters), skip]);

  

    const P1 = (pageData?.size || 0) > 0;

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <Layout
            infoSource={infoSource}
            loading={loading}
            SliderPunShow={false}
            prices={prices}
            handleFilters={handleFilters}
            setMyFilters={setMyFilters}
            P1={P1}
            {...infoSource?.allSettings?.layout}
        >
            <div style={infoSource.styleNav}>
                <DynamicPageRenderer
                    facets={facets}
                    pageId={PAGE_ID}
                    pageData={pageData}
                    infoSource={infoSource}
                    laguangeElakay={laguangeElakay}
                    homeStyles={homeStyles}
                     page="bestSellers"
                    totalKey="bestSellersTotal"
                    myFilters={myFilters}
                    reloadFacets={reloadFacets}
                    configOverrides={configOverrides}
                />

                {loading && !pageData?.productsBySell?.length && (
                    <>
                        <LoadingSwiper myStyles={homeStyles} />
                        <LoadingSwiper myStyles={homeStyles} />
                    </>
                )}
            </div>
         </Layout>
    );
};

export default BestSellers;
